import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { maskCep, maskPhone } from "../../../utils/masks";
import { useContextSite } from "../../../context/Context";
import { useUfs } from "../../../hooks/queries/ibge/useUfs/useUfs";
import { useCidadesPorEstado } from "../../../hooks/queries/ibge/useCidadesPorEstado/useCidadesPorEstado";
import { useViaCep } from "../../../hooks/queries/viaCep/useViaCep/useViaCep";
import { Tracking } from "../../../services/Tracking";
import { IAtendimentoDomiciliarForm } from "../../../types/agendamento";

export type IFormDeliveryResidenceV2Values = Omit<
  IAtendimentoDomiciliarForm,
  "uuid"
>;

const schemaEndereco = z.object({
  bairro: z.string().min(1, "Campo obrigatorio"),
  cep: z.string().min(9, "Campo obrigatorio"),
  cidade: z.string().min(2, "Campo obrigatorio"),
  complemento: z.string().optional().or(z.literal("")),
  logradouro: z.string().min(1, "Campo obrigatorio"),
  numero: z.string().min(1, "Campo obrigatorio"),
  uf: z.string().min(2, "Campo obrigatorio"),
});

const schema = z.object({
  nome: z.string().min(1, "Campo obrigatorio"),
  telefone: z.string().min(9, "Campo obrigatorio"),
  endereco: schemaEndereco,
});

export const useFormDeliveryResidenceV2 = () => {
  const { setIsLoad } = useContextSite();
  const { buscarEndereco } = useViaCep();
  const [cidadeTemp, setCidadeTemp] = useState("");

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { errors },
  } = useForm<IFormDeliveryResidenceV2Values>({
    defaultValues: {
      nome: "",
      telefone: "",
      endereco: {
        bairro: "",
        cep: "",
        cidade: "",
        complemento: "",
        logradouro: "",
        numero: "",
        uf: "",
      },
    },
    resolver: zodResolver(schema),
    mode: "all",
  });

  const ufSelecionada = watch("endereco.uf");
  const cepDigitado = watch("endereco.cep");
  const telefoneDigitado = watch("telefone");

  const { data: ufsData, isError: ufsComErro } = useUfs();
  const ufOptions = useMemo(
    () => (ufsData ?? []).map((i) => ({ value: i.sigla, label: i.sigla, element: i })),
    [ufsData],
  );

  const { data: cidadesData, isError: cidadesComErro } =
    useCidadesPorEstado(ufSelecionada);
  const cidades = useMemo(
    () =>
      (cidadesData ?? []).map((item) => ({
        value: item.nome,
        label: item.nome,
        element: item,
      })),
    [cidadesData],
  );

  useEffect(() => {
    if (ufsComErro) {
      Tracking.error("mapa:agendamento/domicilio:buscar-ufs-falhou");
      toast.error("Erro ao buscar a lista de estados");
    }
  }, [ufsComErro]);

  useEffect(() => {
    if (cidadesComErro) {
      Tracking.error("mapa:agendamento/domicilio:buscar-cidades-falhou");
      toast.error("Erro ao buscar a lista de cidades");
    }
  }, [cidadesComErro]);

  useEffect(() => {
    if (ufSelecionada) {
      setValue("endereco.cidade", "");
    }
  }, [ufSelecionada, setValue]);

  useEffect(() => {
    if (cidadesData && cidadeTemp) {
      setValue("endereco.cidade", cidadeTemp);
      setCidadeTemp("");
    }
  }, [cidadesData, cidadeTemp, setValue]);

  useEffect(() => {
    setValue("endereco.cep", maskCep(cepDigitado));
  }, [cepDigitado, setValue]);

  useEffect(() => {
    setValue("telefone", maskPhone(telefoneDigitado));
  }, [telefoneDigitado, setValue]);

  const handleCep = () => {
    const cep = watch("endereco.cep");
    const cepCompleto = cep.length === 9;

    if (!cepCompleto) return;

    setIsLoad(true);
    buscarEndereco(cep)
      .then((data) => {
        setValue("endereco.logradouro", data.logradouro);
        setValue("endereco.bairro", data.bairro);
        setValue("endereco.uf", data.uf);
        setCidadeTemp(data.localidade);
      })
      .catch(() => {
        Tracking.error("mapa:agendamento/domicilio:buscar-cep-falhou");
        toast.error("Cep não encontrado");
      })
      .finally(() => setIsLoad(false));
  };

  return {
    control,
    handleSubmit,
    register,
    errors,
    watch,
    ufOptions,
    cidades,
    handleCep,
  };
};
